import './i18n/autoTranslate';
// PastInterviewsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AreaChart,
  Area,
  ComposedChart,
  Line,
  BarChart,
  Bar,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  ArrowUpRight,
  Calendar,
  ChevronDown,
  Clock,
  Filter,
  History,
  Layers,
  Loader2,
  Search,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  X,
  Zap,
} from "lucide-react";

// -----------------------------------------------------
// Config
// -----------------------------------------------------
const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5000";

function getAccessToken() {
  return localStorage.getItem("access_token") || localStorage.getItem("token") || "";
}

// FIX: support AbortSignal so detail-fetch races don't overwrite newer data
async function fetchJson(path, { method = "GET", body, signal } = {}) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
    signal,
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg =
      data?.error ||
      data?.message ||
      data?.detail ||
      `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

function clamp(n, a, b) {
  const x = Number(n);
  if (Number.isNaN(x)) return a;
  return Math.max(a, Math.min(b, x));
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function startOfDay(dt) {
  const d = new Date(dt);
  d.setHours(0, 0, 0, 0);
  return d;
}

function keyDay(dt) {
  const d = startOfDay(dt);
  return d.toISOString().slice(0, 10);
}

function prettyDayLabel(isoDay) {
  const d = new Date(isoDay);
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
}

function safeScore(interview) {
  const a = interview?.performance_score;
  const b = interview?.overall_score;
  const v = a ?? b;
  const num = Number(v);
  return Number.isFinite(num) ? num : null;
}

function safeStatus(interview) {
  return (interview?.status || "configured").toLowerCase();
}

function safeStr(v) {
  return (v ?? "").toString().trim();
}

// FIX: title-case helper for chart labels & pills (was showing raw "behavioral")
function prettyLabel(s) {
  if (!s) return "—";
  return String(s)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isPlainObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function normalizeArray(v) {
  if (Array.isArray(v)) return v;
  if (v == null) return [];
  if (typeof v === "string") return v.trim() ? [v.trim()] : [];
  if (isPlainObject(v)) {
    if (Array.isArray(v.items)) return v.items;
    return Object.values(v);
  }
  return [];
}

function normalizeText(v) {
  if (v == null) return null;
  if (typeof v === "string") return v.trim() || null;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) {
    const parts = v
      .map((x) =>
        typeof x === "string"
          ? x
          : isPlainObject(x)
          ? (x.text || x.title || x.message || JSON.stringify(x))
          : String(x)
      )
      .filter(Boolean);
    return parts.length ? parts.join("\n") : null;
  }
  if (isPlainObject(v)) {
    const pick = v.text || v.message || v.content || v.value || v.summary;
    if (typeof pick === "string") return pick.trim() || null;
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function Paragraphs({ value, className = "" }) {
  const txt = normalizeText(value);
  if (!txt) return <div className={"text-sm text-white/55 " + className}>—</div>;

  const parts = String(txt).split(/\n+/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className={"space-y-2 " + className}>
      {parts.map((p, i) => (
        <p key={i} className="text-sm text-white/70 leading-relaxed">
          {p}
        </p>
      ))}
    </div>
  );
}

function pillTone(status) {
  switch (status) {
    case "completed":
      return "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-500/25";
    case "in_progress":
      return "bg-sky-500/15 text-sky-200 ring-1 ring-sky-500/25";
    case "cancelled":
      return "bg-zinc-500/15 text-zinc-200 ring-1 ring-zinc-500/25";
    default:
      return "bg-amber-500/15 text-amber-200 ring-1 ring-amber-500/25";
  }
}

function gradeFromScore(score) {
  if (score == null) return "—";
  if (score >= 95) return "A+";
  if (score >= 90) return "A";
  if (score >= 85) return "A-";
  if (score >= 80) return "B+";
  if (score >= 75) return "B";
  if (score >= 70) return "B-";
  if (score >= 65) return "C+";
  if (score >= 60) return "C";
  if (score >= 55) return "C-";
  if (score >= 50) return "D";
  return "F";
}

function computeTimeSeries(interviews, days = 30) {
  const now = new Date();
  const map = new Map();

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const k = keyDay(d);
    map.set(k, { day: k, label: prettyDayLabel(k), score: 0, sessions: 0 });
  }

  (interviews || []).forEach((it) => {
    const status = safeStatus(it);
    if (status !== "completed") return;
    const when = it?.completed_at || it?.created_at;
    if (!when) return;
    const k = keyDay(new Date(when));
    if (!map.has(k)) return;
    const s = safeScore(it);
    if (s == null) return;
    const row = map.get(k);
    row.score += s;
    row.sessions += 1;
  });

  return Array.from(map.values()).map((r) => ({
    ...r,
    avgScore: r.sessions ? Math.round((r.score / r.sessions) * 10) / 10 : 0,
  }));
}

// FIX: drop the "unknown" bucket so the chart isn't dominated by missing data
function computeTypeDist(interviews) {
  const dist = {};
  (interviews || []).forEach((it) => {
    const t = safeStr(it?.interview_type || it?.configuration?.interview_type).toLowerCase();
    if (!t) return;
    dist[t] = (dist[t] || 0) + 1;
  });
  return Object.entries(dist)
    .map(([name, value]) => ({ name, value, label: prettyLabel(name) }))
    .sort((a, b) => b.value - a.value);
}

function computeFormatDist(interviews) {
  const dist = {};
  (interviews || []).forEach((it) => {
    const f = safeStr(it?.selected_format || it?.configuration?.selected_format).toLowerCase();
    if (!f) return;
    dist[f] = (dist[f] || 0) + 1;
  });
  return Object.entries(dist)
    .map(([name, value]) => ({ name, value, label: prettyLabel(name) }))
    .sort((a, b) => b.value - a.value);
}

function SoftCard({ className = "", children }) {
  return (
    <div
      className={[
        "rounded-2xl bg-white/[0.04] ring-1 ring-white/10 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function StatPill({ label, value, hint, icon: Icon, accent = "from-white/15 to-white/5" }) {
  return (
    <SoftCard className="p-4">
      <div className="flex items-start gap-3">
        <div
          className={`h-10 w-10 rounded-xl bg-gradient-to-b ${accent} ring-1 ring-white/10 flex items-center justify-center shrink-0`}
        >
          <Icon className="h-5 w-5 text-white/80" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-white/60">{label}</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-2xl font-semibold text-white">{value}</div>
            {hint ? (
              <div className="text-xs text-white/50 truncate">{hint}</div>
            ) : null}
          </div>
        </div>
      </div>
    </SoftCard>
  );
}

function ProgressBar({ value, max = 100 }) {
  const pct = max > 0 ? clamp((value / max) * 100, 0, 100) : 0;
  return (
    <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-emerald-400/90 via-sky-400/90 to-violet-400/90 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// FIX: tooltip now color-codes each entry so multi-series charts are readable
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-zinc-950/90 ring-1 ring-white/10 px-3 py-2 backdrop-blur-xl shadow-xl">
      {label != null ? <div className="text-xs text-white/60 mb-1">{label}</div> : null}
      <div className="space-y-0.5">
        {payload.map((p, idx) => (
          <div key={idx} className="text-sm text-white flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-full shrink-0"
              style={{ background: p.color || p.stroke || p.fill || "#fff" }}
            />
            <span className="text-white/65">{p.name}:</span>
            <span className="font-medium tabular-nums">{p.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <SoftCard className="p-8">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-white/5 ring-1 ring-white/10 flex items-center justify-center shrink-0">
          <History className="h-5 w-5 text-white/70" />
        </div>
        <div>
          <div className="text-white font-semibold">{title}</div>
          <div className="text-sm text-white/60 mt-1">{subtitle}</div>
        </div>
      </div>
    </SoftCard>
  );
}

function MetricMini({ label, value, suffix = "", goodFrom = 80 }) {
  const v = value == null ? null : Number(value);
  const ok = v != null && Number.isFinite(v) && v >= goodFrom;
  return (
    <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3">
      <div className="text-xs text-white/55">{label}</div>
      <div className="mt-1 flex items-baseline gap-2 flex-wrap">
        <div className="text-lg font-semibold text-white tabular-nums">
          {v == null ? "—" : `${Math.round(v)}${suffix}`}
        </div>
        <div
          className={`text-[11px] ${
            v == null ? "text-white/35" : ok ? "text-emerald-300/80" : "text-amber-300/80"
          }`}
        >
          {v == null ? "n/a" : ok ? "strong" : "needs work"}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------
// Main Page
// -----------------------------------------------------
export default function PastInterviewsPage() {
  const [loading, setLoading] = useState(true);
  const [busyDetails, setBusyDetails] = useState(false);
  const [error, setError] = useState("");

  const [interviews, setInterviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);
  const [dash, setDash] = useState(null);
  const [activities, setActivities] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);

  // filters
  const [rangeDays, setRangeDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  // -----------------------------------------------------
  // Load page data
  // -----------------------------------------------------
  useEffect(() => {
    const ctrl = new AbortController();
    let mounted = true;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const [listRes, statsRes, dashRes, actRes] = await Promise.all([
          fetchJson("/api/interview", { signal: ctrl.signal }),
          fetchJson("/api/interview/statistics", { signal: ctrl.signal }),
          fetchJson("/api/interview/analytics/dashboard", { signal: ctrl.signal }),
          fetchJson("/api/interview/activities", { signal: ctrl.signal }),
        ]);

        // subscription usage is optional (free users might not have it)
        let usageRes = null;
        try {
          usageRes = await fetchJson("/api/subscriptions/my-subscription/usage", {
            signal: ctrl.signal,
          });
        } catch {
          usageRes = null;
        }

        if (!mounted) return;

        const list = Array.isArray(listRes?.interviews) ? listRes.interviews : [];
        setInterviews(list);
        setStats(statsRes?.statistics || null);
        setDash(dashRes || null);
        setActivities(Array.isArray(actRes?.activities) ? actRes.activities : []);
        setUsage(usageRes?.usage || null);

        // auto-select the most recent completed interview
        const firstCompleted = list.find((i) => safeStatus(i) === "completed");
        if (firstCompleted) setSelectedId(firstCompleted.id);
        else if (list.length) setSelectedId(list[0].id);
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (mounted) setError(e?.message || "Failed to load interviews");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, []);

  // -----------------------------------------------------
  // Load selected interview details
  // FIX: AbortController per fetch; if user clicks fast, only the latest
  // request resolves into state (no stale-detail flash).
  // -----------------------------------------------------
  const detailReqRef = useRef(null);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }

    // cancel any in-flight detail request before starting a new one
    if (detailReqRef.current) detailReqRef.current.abort();
    const ctrl = new AbortController();
    detailReqRef.current = ctrl;

    let mounted = true;
    (async () => {
      setBusyDetails(true);
      try {
        const res = await fetchJson(`/api/interview/${selectedId}`, { signal: ctrl.signal });
        if (!mounted || ctrl.signal.aborted) return;
        // FIX: detail endpoint may return the interview either at the top
        // level or wrapped under `interview` / `data`. Try all shapes before
        // falling back to the raw response.
        const interview =
          res?.interview ||
          res?.data?.interview ||
          res?.data ||
          (res && typeof res === "object" && "id" in res ? res : null);
        setSelected(interview);
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (mounted) setSelected(null);
      } finally {
        if (mounted && !ctrl.signal.aborted) setBusyDetails(false);
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [selectedId]);

  // -----------------------------------------------------
  // Derived
  // -----------------------------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = new Date();
    const minDate = new Date(now);
    minDate.setDate(minDate.getDate() - rangeDays);

    return (interviews || []).filter((it) => {
      const st = safeStatus(it);
      if (statusFilter !== "all" && st !== statusFilter) return false;

      // FIX: only filter by date if we *have* a date — don't drop sessions
      // that are configured but not yet timestamped.
      const when = it?.completed_at || it?.created_at;
      if (when) {
        const d = new Date(when);
        if (!Number.isNaN(d.getTime()) && d < minDate) return false;
      }

      if (!q) return true;

      const role = safeStr(it?.selected_role || it?.configuration?.selected_role);
      const industry = safeStr(it?.selected_industry || it?.configuration?.selected_industry);
      const format = safeStr(it?.selected_format || it?.configuration?.selected_format);
      const type = safeStr(it?.interview_type || it?.configuration?.interview_type);
      const persona = safeStr(it?.ai_persona_name || it?.configuration?.ai_persona_name);

      const hay = `${role} ${industry} ${format} ${type} ${persona} ${st}`.toLowerCase();
      return hay.includes(q);
    });
  }, [interviews, rangeDays, statusFilter, search]);

  const completed = useMemo(
    () => filtered.filter((i) => safeStatus(i) === "completed"),
    [filtered]
  );

  const avgScore = useMemo(() => {
    const scores = completed.map(safeScore).filter((x) => x != null);
    if (!scores.length) return null;
    return Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10;
  }, [completed]);

  const bestScore = useMemo(() => {
    const scores = completed.map(safeScore).filter((x) => x != null);
    return scores.length ? Math.max(...scores) : null;
  }, [completed]);

  const timeSeries = useMemo(
    () => computeTimeSeries(filtered, rangeDays),
    [filtered, rangeDays]
  );
  const typeDist = useMemo(() => computeTypeDist(filtered), [filtered]);
  const formatDist = useMemo(() => computeFormatDist(filtered), [filtered]);

  const weeklyLine = useMemo(() => {
    const w = dash?.weekly_data;
    return Array.isArray(w) ? w : [];
  }, [dash]);

  const skillsRadar = useMemo(() => {
    const s = dash?.skills_data;
    if (!Array.isArray(s) || !s.length) return [];
    return s.map((x) => ({
      skill: prettyLabel(x.name),
      score: clamp(x.value, 0, 100),
    }));
  }, [dash]);

  const usagePct = useMemo(() => {
    if (!usage) return null;
    const used = Number(usage.interviews_used ?? 0);
    const limit = Number(usage.interviews_limit ?? 0);
    if (!limit) return 0;
    return Math.round(((used / limit) * 100) * 10) / 10;
  }, [usage]);

  // Selected metrics from detail (best-effort)
  const detailMetrics = useMemo(() => {
    if (!selected) {
      return {
        score: null, grade: "—", status: "", created_at: null, completed_at: null,
        duration: null, format: null, interview_type: null, role: null, industry: null,
        company_type: null, persona: null, difficulty: null, tone: null,
        followups: undefined, surprise: undefined, star_enforced: undefined,
        follow_intensity: null, adaptive: undefined,
        speaking_wpm: null, filler_words: null, eye_contact: null, star_used: null,
        total_questions: null, strengths: [], weakest_areas: [],
        feedback_general: null, feedback_detailed: null,
        categories: null, suggestions: [], next_steps: null,
      };
    }

    // FIX: backend returns config/results in different shapes depending on
    // endpoint. The list endpoint puts most fields top-level on the interview
    // object; the detail endpoint sometimes wraps them in `configuration`,
    // sometimes in `results`/`result`/`analysis`/`report`, and sometimes
    // mirrors them top-level too. Read from ALL known locations and pick the
    // first non-null value — same fallback strategy the list cards use.
    //
    // We also merge in the matching list row as a last-resort fallback so the
    // detail panel never shows LESS than the list card already showed.
    const listRow = interviews.find((it) => it.id === selected.id) || {};
    const cfg =
      selected.configuration || selected.config || listRow.configuration || {};
    const res =
      selected.results ||
      selected.result ||
      selected.analysis ||
      selected.report ||
      listRow.results ||
      {};

    // Generic helper: try a list of paths and return the first defined value.
    const pick = (...vals) => {
      for (const v of vals) {
        if (v !== undefined && v !== null && v !== "") return v;
      }
      return null;
    };

    // Configuration fields — check top-level on `selected` first (matches the
    // list endpoint's shape), then `configuration`, then the list row.
    const role = pick(
      selected.selected_role, cfg.selected_role, selected.role,
      listRow.selected_role, listRow.configuration?.selected_role
    );
    const industry = pick(
      selected.selected_industry, cfg.selected_industry, selected.industry,
      listRow.selected_industry, listRow.configuration?.selected_industry
    );
    const format = pick(
      selected.selected_format, cfg.selected_format, selected.format,
      listRow.selected_format, listRow.configuration?.selected_format
    );
    const interview_type = pick(
      selected.interview_type, cfg.interview_type, selected.type,
      listRow.interview_type, listRow.configuration?.interview_type
    );
    const company_type = pick(
      selected.selected_company_type, cfg.selected_company_type, selected.company_type,
      listRow.selected_company_type, listRow.configuration?.selected_company_type
    );
    const persona = pick(
      selected.ai_persona_name, cfg.ai_persona_name, selected.persona,
      listRow.ai_persona_name, listRow.configuration?.ai_persona_name
    );
    const duration = pick(
      selected.duration, cfg.duration,
      selected.duration_minutes, cfg.duration_minutes,
      listRow.duration, listRow.configuration?.duration
    );
    const difficulty = pick(
      selected.ai_difficulty, cfg.ai_difficulty, selected.difficulty,
      listRow.ai_difficulty, listRow.configuration?.ai_difficulty
    );
    const tone = pick(
      selected.ai_tone, cfg.ai_tone, selected.tone,
      listRow.ai_tone, listRow.configuration?.ai_tone
    );
    const followups = pick(
      selected.enable_follow_ups, cfg.enable_follow_ups,
      selected.follow_ups_enabled,
      listRow.enable_follow_ups, listRow.configuration?.enable_follow_ups
    );

    // Score: same fallback chain as safeScore() for the list, plus deeper paths
    const performance = pick(
      selected.performance_score, res.performance_score,
      listRow.performance_score
    );
    const overall = pick(
      selected.overall_score, res.overall_score, cfg.overall_score,
      res.final_score, selected.final_score, res.score, selected.score,
      listRow.overall_score
    );

    // Feedback — string OR object with various property names
    const fb = pick(res.feedback, selected.feedback);
    const fbObj = isPlainObject(fb);

    const feedback_general = fbObj
      ? pick(fb.general, fb.summary, fb.overview)
      : fb;
    const feedback_detailed = fbObj
      ? pick(fb.detailed_feedback, fb.detailed, fb.details)
      : null;

    const strengths = normalizeArray(
      pick(res.strengths, selected.strengths, fbObj ? fb.strengths : null)
    );
    const weakest_areas = normalizeArray(
      pick(
        res.weakest_areas,
        selected.weakest_areas,
        fbObj ? fb.weakest_areas : null
      )
    );

    return {
      score: performance ?? overall ?? null,
      grade: gradeFromScore(performance ?? overall ?? null),
      status: safeStr(selected.status || listRow.status),
      created_at: selected.created_at || listRow.created_at,
      completed_at: selected.completed_at || listRow.completed_at,

      duration,
      format,
      interview_type,
      role,
      industry,
      company_type,
      persona,
      difficulty,
      tone,
      followups,
      surprise: pick(selected.surprise_questions, cfg.surprise_questions),
      star_enforced: pick(
        selected.star_method_enforcement,
        cfg.star_method_enforcement
      ),
      follow_intensity: pick(
        selected.follow_up_intensity,
        cfg.follow_up_intensity
      ),
      adaptive: pick(selected.adaptive_difficulty, cfg.adaptive_difficulty),

      speaking_wpm: pick(res.speaking_pace_wpm, selected.speaking_pace_wpm),
      filler_words: pick(res.filler_word_count, selected.filler_word_count),
      eye_contact: pick(
        res.eye_contact_percentage,
        selected.eye_contact_percentage
      ),
      star_used: pick(
        res.star_method_usage_count,
        selected.star_method_usage_count
      ),
      total_questions: pick(
        res.total_questions_asked,
        selected.total_questions_asked
      ),

      strengths,
      weakest_areas,

      feedback_general,
      feedback_detailed,

      categories: pick(
        res.performance_categories,
        selected.performance_categories
      ),
      suggestions: normalizeArray(
        pick(
          res.improvement_suggestions,
          res.suggestions,
          selected.improvement_suggestions,
          selected.suggestions
        )
      ),
      next_steps: pick(res.next_steps, selected.next_steps),
    };
  }, [selected, interviews]);

  // -----------------------------------------------------
  // Actions
  // -----------------------------------------------------
  async function deleteInterview(id) {
    const ok = window.confirm("Delete this interview session? This cannot be undone.");
    if (!ok) return;
    try {
      await fetchJson(`/api/interview/${id}`, { method: "DELETE" });
      setInterviews((prev) => prev.filter((x) => x.id !== id));
      if (selectedId === id) setSelectedId(null);
    } catch (e) {
      alert(e?.message || "Delete failed");
    }
  }

  // Distribution palette (cycled per bar)
  const TYPE_COLORS = ["#a78bfa", "#38bdf8", "#34d399", "#f472b6", "#fbbf24", "#f87171"];
  const FORMAT_COLORS = ["#34d399", "#38bdf8", "#a78bfa", "#fbbf24", "#f472b6", "#f87171"];

  // -----------------------------------------------------
  // UI
  // -----------------------------------------------------
  return (
    <div className="min-h-screen bg-[#05070c] text-white">
      {/* Background */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-gradient-to-r from-violet-500/25 via-sky-500/20 to-emerald-500/15 blur-3xl" />
        <div className="absolute bottom-[-240px] right-[-240px] h-[520px] w-[520px] rounded-full bg-gradient-to-r from-fuchsia-500/15 via-indigo-500/15 to-cyan-500/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.06),transparent_45%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.04),transparent_50%)]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/5 ring-1 ring-white/10 px-3 py-1 text-xs text-white/70">
              <Sparkles className="h-3.5 w-3.5" />
              Interview Intelligence
            </div>
            <h1 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight">
              Past Interviews
            </h1>
            <p className="mt-1 text-sm text-white/60">
              Track every session, understand patterns, and watch your performance climb over time.
            </p>
          </div>

          {/* FIX: header controls now wrap cleanly. flex-wrap + sensible widths on each. */}
          <div className="flex flex-wrap gap-3 lg:flex-nowrap lg:items-center">
            {/* Range */}
            <label className="inline-flex items-center gap-2 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-3 py-2 cursor-pointer">
              <Calendar className="h-4 w-4 text-white/70 shrink-0" />
              <span className="sr-only">Date range</span>
              <select
                value={rangeDays}
                onChange={(e) => setRangeDays(Number(e.target.value))}
                aria-label="Date range"
                className="bg-transparent text-sm text-white outline-none cursor-pointer"
              >
                <option className="bg-zinc-950" value={7}>Last 7 days</option>
                <option className="bg-zinc-950" value={30}>Last 30 days</option>
                <option className="bg-zinc-950" value={90}>Last 90 days</option>
              </select>
              <ChevronDown className="h-4 w-4 text-white/50 shrink-0" />
            </label>

            {/* Status */}
            <label className="inline-flex items-center gap-2 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-3 py-2 cursor-pointer">
              <Filter className="h-4 w-4 text-white/70 shrink-0" />
              <span className="sr-only">Status filter</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Status filter"
                className="bg-transparent text-sm text-white outline-none cursor-pointer"
              >
                <option className="bg-zinc-950" value="all">All</option>
                <option className="bg-zinc-950" value="completed">Completed</option>
                <option className="bg-zinc-950" value="in_progress">In progress</option>
                <option className="bg-zinc-950" value="configured">Configured</option>
                <option className="bg-zinc-950" value="cancelled">Cancelled</option>
              </select>
              <ChevronDown className="h-4 w-4 text-white/50 shrink-0" />
            </label>

            {/* Search — flex-1 on mobile so it actually fills the row, sane min on desktop */}
            <div className="flex flex-1 min-w-[200px] sm:min-w-[260px] items-center gap-2 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-3 py-2">
              <Search className="h-4 w-4 text-white/60 shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search role, industry, format, type…"
                aria-label="Search interviews"
                className="w-full bg-transparent text-sm text-white placeholder:text-white/35 outline-none"
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="p-1 rounded-lg hover:bg-white/5 shrink-0"
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4 text-white/50" />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Loading / Error */}
        {loading ? (
          <div className="mt-8 flex items-center gap-3 text-white/70">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading interviews & analytics…
          </div>
        ) : error ? (
          <div className="mt-8">
            <SoftCard className="p-5">
              <div className="text-white font-semibold">Couldn’t load this page</div>
              <div className="text-sm text-white/60 mt-1">{error}</div>
              <div className="text-xs text-white/40 mt-3">
                Tip: confirm your JWT token is present and valid, and that{" "}
                <span className="text-white/60">{API_BASE}</span> is reachable.
              </div>
            </SoftCard>
          </div>
        ) : (
          <>
            {/* Monthly usage */}
            {usage ? (
              <div className="mt-8">
                <SoftCard className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-white/80 flex-wrap">
                        <Zap className="h-4 w-4" />
                        <span className="font-semibold">Monthly Interview Quota</span>
                        <span className="text-xs text-white/40">
                          (resets in {usage.days_until_reset ?? 0} days)
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-white/60">
                        You’ve used{" "}
                        <span className="text-white font-semibold">{usage.interviews_used}</span> /{" "}
                        <span className="text-white font-semibold">{usage.interviews_limit}</span>{" "}
                        interviews this month.
                      </div>
                      <div className="mt-3">
                        <ProgressBar
                          value={usage.interviews_used}
                          max={usage.interviews_limit || 1}
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 shrink-0">
                      <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 px-4 py-3">
                        <div className="text-xs text-white/55">Remaining</div>
                        <div className="mt-1 text-lg font-semibold text-white tabular-nums">
                          {usage.interviews_remaining ??
                            Math.max(
                              (usage.interviews_limit || 0) - (usage.interviews_used || 0),
                              0
                            )}
                        </div>
                      </div>
                      <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 px-4 py-3">
                        <div className="text-xs text-white/55">Usage</div>
                        <div className="mt-1 text-lg font-semibold text-white tabular-nums">
                          {usagePct ?? 0}%
                        </div>
                      </div>
                    </div>
                  </div>
                </SoftCard>
              </div>
            ) : null}

            {/* Top stats */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatPill
                label="Total sessions"
                value={filtered.length}
                hint={`${completed.length} completed`}
                icon={Layers}
                accent="from-sky-500/25 to-white/5"
              />
              <StatPill
                label="Avg score (completed)"
                value={avgScore == null ? "—" : avgScore}
                hint={`Best: ${bestScore == null ? "—" : bestScore}`}
                icon={Target}
                accent="from-emerald-500/25 to-white/5"
              />
              <StatPill
                label="Weekly trend"
                value={
                  dash?.weekly_trend != null
                    ? `${dash.weekly_trend > 0 ? "+" : ""}${dash.weekly_trend}%`
                    : "—"
                }
                hint="vs previous week"
                icon={TrendingUp}
                accent="from-violet-500/25 to-white/5"
              />
              <StatPill
                label="Completed interviews"
                value={stats?.completed_interviews ?? completed.length}
                hint={`In progress: ${
                  stats?.in_progress ??
                  filtered.filter((i) => safeStatus(i) === "in_progress").length
                }`}
                icon={Trophy}
                accent="from-fuchsia-500/25 to-white/5"
              />
            </div>

            {/* Charts row 1 */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Progress over time */}
              <SoftCard className="p-5 lg:col-span-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-white font-semibold">Progress over time</div>
                    <div className="text-sm text-white/55 mt-1">
                      Daily average score for completed interviews (last {rangeDays} days)
                    </div>
                  </div>
                  <div className="text-xs text-white/50 flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Updated live
                  </div>
                </div>

                {/*
                  FIX: was an <AreaChart> with a <Line> child — recharts ignores
                  <Line> inside <AreaChart>, so the "sessions" series never
                  rendered. Switched to <ComposedChart> which natively supports
                  mixed Area + Line. Also added a SECOND Y-axis for sessions so
                  it isn't squashed against the bottom of the score axis.
                */}
                <div className="mt-4 h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={timeSeries}
                      margin={{ left: 8, right: 8, top: 10, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="rgba(56,189,248,0.35)" />
                          <stop offset="95%" stopColor="rgba(56,189,248,0.02)" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        minTickGap={16}
                      />
                      <YAxis
                        yAxisId="score"
                        tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        domain={[0, 100]}
                      />
                      <YAxis
                        yAxisId="sessions"
                        orientation="right"
                        tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        content={<ChartTooltip />}
                        cursor={{ stroke: "rgba(255,255,255,0.15)", strokeWidth: 1 }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}
                        iconType="circle"
                      />
                      <Area
                        yAxisId="score"
                        type="monotone"
                        dataKey="avgScore"
                        name="Avg score"
                        stroke="rgba(56,189,248,0.95)"
                        strokeWidth={2}
                        fill="url(#scoreFill)"
                        activeDot={{ r: 4, stroke: "#0ea5e9", strokeWidth: 2, fill: "#0c1220" }}
                        isAnimationActive={false}
                      />
                      <Line
                        yAxisId="sessions"
                        type="monotone"
                        dataKey="sessions"
                        name="Sessions"
                        stroke="rgba(167,139,250,0.95)"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4, stroke: "#a78bfa", strokeWidth: 2, fill: "#0c1220" }}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </SoftCard>

              {/* Skills radar */}
              <SoftCard className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold">Skill radar</div>
                    <div className="text-sm text-white/55 mt-1">Avg from recent interviews</div>
                  </div>
                  <div className="h-9 w-9 rounded-xl bg-white/5 ring-1 ring-white/10 flex items-center justify-center shrink-0">
                    <Sparkles className="h-4 w-4 text-white/70" />
                  </div>
                </div>

                <div className="mt-4 h-[280px]">
                  {skillsRadar.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={skillsRadar} outerRadius="78%">
                        <PolarGrid stroke="rgba(255,255,255,0.08)" />
                        <PolarAngleAxis
                          dataKey="skill"
                          tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 11 }}
                        />
                        <PolarRadiusAxis
                          angle={30}
                          domain={[0, 100]}
                          tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }}
                          axisLine={false}
                        />
                        <Tooltip content={<ChartTooltip />} />
                        <Radar
                          name="Score"
                          dataKey="score"
                          stroke="rgba(52,211,153,0.95)"
                          fill="rgba(52,211,153,0.22)"
                          strokeWidth={2}
                          isAnimationActive={false}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-white/50 text-sm">
                      No skills data yet.
                    </div>
                  )}
                </div>
              </SoftCard>
            </div>

            {/* Charts row 2 */}
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Weekly chart */}
              <SoftCard className="p-5 lg:col-span-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-white font-semibold">This week performance</div>
                    <div className="text-sm text-white/55 mt-1">
                      From /analytics/dashboard (last 7 days)
                    </div>
                  </div>
                  <div className="text-xs text-white/50 inline-flex items-center gap-1">
                    <ArrowUpRight className="h-4 w-4" />
                    Trend: {dash?.weekly_trend != null ? `${dash.weekly_trend}%` : "—"}
                  </div>
                </div>

                {/*
                  FIX: was a single 0–100 axis with both score and sessions on it,
                  so sessions (typically 0–10) collapsed to a flat line at the
                  bottom. Now uses a composed chart with two y-axes.
                */}
                <div className="mt-4 h-[240px]">
                  {weeklyLine.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={weeklyLine}
                        margin={{ left: 8, right: 8, top: 10, bottom: 0 }}
                      >
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis
                          dataKey="day"
                          tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          yAxisId="score"
                          domain={[0, 100]}
                          tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          yAxisId="sessions"
                          orientation="right"
                          tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                          allowDecimals={false}
                        />
                        <Tooltip
                          content={<ChartTooltip />}
                          cursor={{ stroke: "rgba(255,255,255,0.15)", strokeWidth: 1 }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}
                          iconType="circle"
                        />
                        <Line
                          yAxisId="score"
                          type="monotone"
                          dataKey="score"
                          name="Avg score"
                          stroke="rgba(244,114,182,0.95)"
                          strokeWidth={2}
                          dot={{ r: 3, fill: "#0c1220", stroke: "#f472b6", strokeWidth: 2 }}
                          activeDot={{ r: 5, stroke: "#f472b6", strokeWidth: 2, fill: "#0c1220" }}
                          isAnimationActive={false}
                        />
                        <Line
                          yAxisId="sessions"
                          type="monotone"
                          dataKey="sessions"
                          name="Sessions"
                          stroke="rgba(56,189,248,0.95)"
                          strokeWidth={2}
                          dot={{ r: 3, fill: "#0c1220", stroke: "#38bdf8", strokeWidth: 2 }}
                          activeDot={{ r: 5, stroke: "#38bdf8", strokeWidth: 2, fill: "#0c1220" }}
                          isAnimationActive={false}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-white/50 text-sm">
                      No weekly data yet.
                    </div>
                  )}
                </div>
              </SoftCard>

              {/* Distributions */}
              <SoftCard className="p-5">
                <div className="text-white font-semibold">Session mix</div>
                <div className="text-sm text-white/55 mt-1">Interview type & format</div>

                {/* Type distribution */}
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wide text-white/45 mb-1">
                    Type
                  </div>
                  <div className="h-[120px]">
                    {typeDist.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={typeDist.slice(0, 6)}
                          margin={{ left: 8, right: 8, top: 0, bottom: 0 }}
                        >
                          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                          <XAxis
                            dataKey="label"
                            tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            interval={0}
                          />
                          <YAxis
                            tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            allowDecimals={false}
                            width={28}
                          />
                          <Tooltip
                            content={<ChartTooltip />}
                            cursor={{ fill: "rgba(255,255,255,0.04)" }}
                          />
                          <Bar
                            dataKey="value"
                            name="Sessions"
                            radius={[8, 8, 0, 0]}
                            isAnimationActive={false}
                          >
                            {typeDist.slice(0, 6).map((_, i) => (
                              <Cell
                                key={`type-${i}`}
                                fill={TYPE_COLORS[i % TYPE_COLORS.length]}
                                fillOpacity={0.75}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-white/50 text-xs">
                        No type data yet.
                      </div>
                    )}
                  </div>
                </div>

                {/* Format distribution */}
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wide text-white/45 mb-1">
                    Format
                  </div>
                  <div className="h-[120px]">
                    {formatDist.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={formatDist.slice(0, 6)}
                          margin={{ left: 8, right: 8, top: 0, bottom: 0 }}
                        >
                          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                          <XAxis
                            dataKey="label"
                            tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            interval={0}
                          />
                          <YAxis
                            tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 11 }}
                            tickLine={false}
                            axisLine={false}
                            allowDecimals={false}
                            width={28}
                          />
                          <Tooltip
                            content={<ChartTooltip />}
                            cursor={{ fill: "rgba(255,255,255,0.04)" }}
                          />
                          <Bar
                            dataKey="value"
                            name="Sessions"
                            radius={[8, 8, 0, 0]}
                            isAnimationActive={false}
                          >
                            {formatDist.slice(0, 6).map((_, i) => (
                              <Cell
                                key={`fmt-${i}`}
                                fill={FORMAT_COLORS[i % FORMAT_COLORS.length]}
                                fillOpacity={0.75}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center text-white/50 text-xs">
                        No format data yet.
                      </div>
                    )}
                  </div>
                </div>
              </SoftCard>
            </div>

            {/* Main: list + details */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* List */}
              <SoftCard className="p-0 lg:col-span-2 overflow-hidden">
                <div className="p-5 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="text-white font-semibold">Sessions</div>
                    <div className="text-xs text-white/50">
                      Showing <span className="text-white/70">{filtered.length}</span> session(s)
                    </div>
                  </div>
                </div>

                {filtered.length ? (
                  <div className="divide-y divide-white/10 max-h-[720px] overflow-y-auto">
                    {filtered.map((it) => {
                      const st = safeStatus(it);
                      const sc = safeScore(it);
                      const when = it?.completed_at || it?.created_at;
                      const role =
                        safeStr(it?.selected_role || it?.configuration?.selected_role) || "—";
                      const industry =
                        safeStr(it?.selected_industry || it?.configuration?.selected_industry) ||
                        "—";
                      const typ =
                        safeStr(it?.interview_type || it?.configuration?.interview_type) || "—";
                      const fmt =
                        safeStr(it?.selected_format || it?.configuration?.selected_format) || "—";
                      const persona = safeStr(
                        it?.ai_persona_name || it?.configuration?.ai_persona_name
                      );

                      const isActive = selectedId === it.id;

                      return (
                        <button
                          key={it.id}
                          type="button"
                          onClick={() => setSelectedId(it.id)}
                          aria-pressed={isActive}
                          className={[
                            // FIX: clearer "active" state — left accent stripe + tinted bg + ring.
                            "relative w-full text-left p-4 transition",
                            isActive
                              ? "bg-sky-500/[0.08] ring-1 ring-inset ring-sky-400/30"
                              : "hover:bg-white/[0.03]",
                          ].join(" ")}
                        >
                          {isActive && (
                            <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-gradient-to-b from-sky-400 to-violet-400" />
                          )}
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className={`text-xs px-2 py-1 rounded-full ${pillTone(st)}`}
                                >
                                  {prettyLabel(st)}
                                </span>
                                <span className="text-xs text-white/55">{prettyLabel(fmt)}</span>
                                <span className="text-xs text-white/35">•</span>
                                <span className="text-xs text-white/55">{prettyLabel(typ)}</span>
                              </div>

                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <div className="font-semibold text-white truncate">{role}</div>
                                <span className="text-xs text-white/35">—</span>
                                <div className="text-sm text-white/60 truncate">{industry}</div>
                              </div>

                              <div className="mt-2 text-xs text-white/45 flex flex-wrap items-center gap-2">
                                <span>
                                  {fmtDate(when)} • {fmtTime(when)}
                                </span>
                                {persona ? (
                                  <>
                                    <span className="text-white/25">•</span>
                                    <span className="inline-flex items-center gap-1">
                                      <Sparkles className="h-3.5 w-3.5" /> {persona}
                                    </span>
                                  </>
                                ) : null}
                              </div>
                            </div>

                            <div className="shrink-0 flex flex-col items-end gap-2">
                              <div className="text-right">
                                <div className="text-xs text-white/50">Score</div>
                                <div className="text-lg font-semibold text-white tabular-nums">
                                  {sc == null ? "—" : Math.round(sc)}
                                </div>
                                <div className="text-xs text-white/45">{gradeFromScore(sc)}</div>
                              </div>

                              <span className="text-[11px] text-white/45 bg-white/5 ring-1 ring-white/10 px-2 py-1 rounded-full">
                                ID #{it.id}
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-5">
                    <EmptyState
                      title="No interviews found"
                      subtitle="Try widening the date range, switching status, or clearing search."
                    />
                  </div>
                )}
              </SoftCard>

              {/* Details */}
              <SoftCard className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white font-semibold">Interview details</div>
                    <div className="text-sm text-white/55 mt-1">
                      Deep metrics + configuration + insights
                    </div>
                  </div>
                  {selectedId ? (
                    <button
                      type="button"
                      onClick={() => setSelectedId(null)}
                      className="h-9 w-9 rounded-xl bg-white/5 ring-1 ring-white/10 hover:bg-white/10 flex items-center justify-center shrink-0"
                      title="Close"
                      aria-label="Close details"
                    >
                      <X className="h-4 w-4 text-white/70" />
                    </button>
                  ) : null}
                </div>

                <div className="mt-4">
                  {busyDetails ? (
                    <div className="flex items-center gap-2 text-white/70">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading details…
                    </div>
                  ) : !selected ? (
                    <div className="text-sm text-white/55">
                      Select an interview to see full details.
                    </div>
                  ) : (
                    <>
                      {/* Headline */}
                      <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${pillTone(
                                  detailMetrics.status?.toLowerCase()
                                )}`}
                              >
                                {prettyLabel(detailMetrics.status || "—")}
                              </span>
                              <span className="text-xs text-white/50">Session #{selected.id}</span>
                              <span className="text-xs text-white/35">•</span>
                              <span className="text-xs text-white/55">
                                {prettyLabel(detailMetrics.format) || "—"}
                              </span>
                              <span className="text-xs text-white/35">•</span>
                              <span className="text-xs text-white/55">
                                {prettyLabel(detailMetrics.interview_type) || "—"}
                              </span>
                            </div>

                            <div className="mt-2 text-lg font-semibold text-white truncate">
                              {detailMetrics.role || "Untitled role"}
                            </div>
                            <div className="mt-1 text-sm text-white/60 truncate">
                              {detailMetrics.industry || "—"}{" "}
                              {detailMetrics.company_type ? `• ${detailMetrics.company_type}` : ""}
                            </div>

                            <div className="mt-3 text-xs text-white/45 leading-relaxed">
                              Created: {fmtDate(detailMetrics.created_at)} •{" "}
                              {fmtTime(detailMetrics.created_at)}
                              <br />
                              Completed: {fmtDate(detailMetrics.completed_at)} •{" "}
                              {fmtTime(detailMetrics.completed_at)}
                            </div>
                          </div>

                          <div className="shrink-0 text-right">
                            <div className="text-xs text-white/55">Score</div>
                            <div className="text-3xl font-semibold text-white tabular-nums">
                              {detailMetrics.score == null
                                ? "—"
                                : Math.round(detailMetrics.score)}
                            </div>
                            <div className="text-xs text-white/45">
                              Grade: {detailMetrics.grade}
                            </div>
                          </div>
                        </div>

                        {/* Quick toggles / features */}
                        <div className="mt-4 flex flex-wrap gap-2">
                          {detailMetrics.persona ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-white/70 inline-flex items-center gap-1">
                              <Sparkles className="h-3.5 w-3.5" />
                              {detailMetrics.persona}
                            </span>
                          ) : null}
                          <span className="text-xs px-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-white/70">
                            Duration: {detailMetrics.duration ?? "—"}m
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-white/70">
                            Difficulty: {prettyLabel(detailMetrics.difficulty) || "—"}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-white/70">
                            Tone: {prettyLabel(detailMetrics.tone) || "—"}
                          </span>
                          {detailMetrics.followups ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/25 text-emerald-200">
                              Follow-ups enabled
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-white/55">
                              No follow-ups
                            </span>
                          )}
                          {detailMetrics.star_enforced ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-violet-500/15 ring-1 ring-violet-500/25 text-violet-200">
                              STAR enforced
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {/* Performance signals */}
                      <div className="mt-4">
                        <div className="text-sm font-semibold text-white">Performance signals</div>
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <MetricMini
                            label="Speaking pace"
                            value={detailMetrics.speaking_wpm}
                            suffix=" WPM"
                            goodFrom={120}
                          />
                          <MetricMini
                            label="Filler words"
                            value={detailMetrics.filler_words}
                            suffix=""
                            goodFrom={0}
                          />
                          <MetricMini
                            label="Eye contact"
                            value={detailMetrics.eye_contact}
                            suffix="%"
                            goodFrom={70}
                          />
                          <MetricMini
                            label="STAR usage"
                            value={detailMetrics.star_used}
                            suffix=""
                            goodFrom={1}
                          />
                          <MetricMini
                            label="Questions asked"
                            value={detailMetrics.total_questions}
                            suffix=""
                            goodFrom={5}
                          />
                          <MetricMini
                            label="Follow-up intensity"
                            value={detailMetrics.follow_intensity}
                            suffix="/5"
                            goodFrom={3}
                          />
                        </div>
                        <div className="mt-2 text-[11px] text-white/45">
                          * Some metrics appear only for completed sessions that submitted results.
                        </div>
                      </div>

                      {/* Category breakdown */}
                      <div className="mt-5">
                        <div className="text-sm font-semibold text-white">Category breakdown</div>
                        <div className="mt-3">
                          {detailMetrics.categories &&
                          typeof detailMetrics.categories === "object" ? (
                            <div className="space-y-2">
                              {Object.entries(detailMetrics.categories)
                                .slice(0, 8)
                                .map(([k, v]) => {
                                  const score = Number(v?.score);
                                  const pct = Number.isFinite(score) ? clamp(score, 0, 100) : 0;
                                  return (
                                    <div
                                      key={k}
                                      className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3"
                                    >
                                      <div className="flex items-center justify-between gap-3">
                                        <div className="text-xs text-white/70 capitalize truncate">
                                          {k.replaceAll("_", " ")}
                                        </div>
                                        <div className="text-xs text-white/60 tabular-nums shrink-0">
                                          {Number.isFinite(score) ? `${Math.round(score)}` : "—"}
                                        </div>
                                      </div>
                                      <div className="mt-2">
                                        <ProgressBar value={pct} max={100} />
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          ) : (
                            <div className="text-sm text-white/55">
                              No category breakdown yet.
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Insights */}
                      <div className="mt-5 space-y-3">
                        <div className="text-sm font-semibold text-white">Insights</div>

                        <div className="grid grid-cols-1 gap-3">
                          <SoftCard className="p-4">
                            <div className="text-xs text-white/60">Strengths</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {(Array.isArray(detailMetrics.strengths)
                                ? detailMetrics.strengths
                                : []
                              )
                                .slice(0, 6)
                                .map((s, idx) => (
                                  <span
                                    key={idx}
                                    className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/25 text-emerald-200"
                                  >
                                    {typeof s === "string" ? s : s?.title || "Strength"}
                                  </span>
                                ))}
                              {!detailMetrics.strengths?.length ? (
                                <span className="text-sm text-white/55">—</span>
                              ) : null}
                            </div>
                          </SoftCard>

                          <SoftCard className="p-4">
                            <div className="text-xs text-white/60">Weakest areas</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {(Array.isArray(detailMetrics.weakest_areas)
                                ? detailMetrics.weakest_areas
                                : []
                              )
                                .slice(0, 6)
                                .map((s, idx) => (
                                  <span
                                    key={idx}
                                    className="text-xs px-2 py-1 rounded-full bg-amber-500/15 ring-1 ring-amber-500/25 text-amber-200"
                                  >
                                    {typeof s === "string" ? s : s?.title || "Area"}
                                  </span>
                                ))}
                              {!detailMetrics.weakest_areas?.length ? (
                                <span className="text-sm text-white/55">—</span>
                              ) : null}
                            </div>
                          </SoftCard>

                          <SoftCard className="p-4">
                            <div className="text-xs text-white/60">Recommendations</div>
                            <div className="mt-2 space-y-2">
                              {(Array.isArray(detailMetrics.suggestions)
                                ? detailMetrics.suggestions
                                : []
                              )
                                .slice(0, 4)
                                .map((x, idx) => (
                                  <div
                                    key={idx}
                                    className="text-sm text-white/70 leading-relaxed"
                                  >
                                    •{" "}
                                    {typeof x === "string"
                                      ? x
                                      : x?.action || x?.title || "Recommendation"}
                                  </div>
                                ))}
                              {!detailMetrics.suggestions?.length ? (
                                <div className="text-sm text-white/55">—</div>
                              ) : null}
                            </div>
                          </SoftCard>

                          {detailMetrics.feedback_general || detailMetrics.feedback_detailed ? (
                            <SoftCard className="p-4">
                              <div className="text-xs text-white/60">Feedback</div>

                              {detailMetrics.feedback_general ? (
                                <div className="mt-2">
                                  <div className="text-[11px] uppercase tracking-wide text-white/45">
                                    General
                                  </div>
                                  <Paragraphs
                                    value={detailMetrics.feedback_general}
                                    className="mt-2"
                                  />
                                </div>
                              ) : null}

                              {detailMetrics.feedback_detailed ? (
                                <div className="mt-4">
                                  <div className="text-[11px] uppercase tracking-wide text-white/45">
                                    Detailed feedback
                                  </div>
                                  <Paragraphs
                                    value={detailMetrics.feedback_detailed}
                                    className="mt-2"
                                  />
                                </div>
                              ) : null}
                            </SoftCard>
                          ) : null}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                          type="button"
                          onClick={() => deleteInterview(selected.id)}
                          className="w-full sm:w-auto rounded-2xl bg-white/5 ring-1 ring-white/10 hover:bg-white/10 px-4 py-2 text-sm text-white/80 transition"
                        >
                          Delete session
                        </button>
                        {/* FIX: was an <a href="#"> with preventDefault — that's a button. */}
                        <button
                          type="button"
                          onClick={() => {
                            // hook up to your full-report route here
                          }}
                          className="w-full sm:w-auto rounded-2xl bg-gradient-to-r from-emerald-400/20 via-sky-400/15 to-violet-400/20 ring-1 ring-white/10 hover:ring-white/20 px-4 py-2 text-sm text-white/90 transition inline-flex items-center justify-center gap-2"
                          title="Open full report"
                        >
                          View full report <ArrowUpRight className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </SoftCard>
            </div>

            {/* Activity feed + Achievements */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SoftCard className="p-5 lg:col-span-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-white font-semibold">Recent activity</div>
                    <div className="text-sm text-white/55 mt-1">Last 50 events</div>
                  </div>
                  <div className="text-xs text-white/50">Auto-updates with new sessions</div>
                </div>

                <div className="mt-4 space-y-2">
                  {activities?.length ? (
                    activities.slice(0, 10).map((a, idx) => (
                      <div
                        key={a.id || `${a.created_at}-${a.type}-${idx}`}
                        className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm text-white/80 font-medium truncate">
                              {a.title || prettyLabel(a.type) || "Activity"}
                            </div>
                            <div className="text-xs text-white/55 mt-1 line-clamp-2">
                              {a.description || "—"}
                            </div>
                          </div>
                          <div className="text-xs text-white/45 shrink-0 whitespace-nowrap">
                            {fmtDate(a.created_at)} {fmtTime(a.created_at)}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-white/55">No activity yet.</div>
                  )}
                </div>
              </SoftCard>

              <SoftCard className="p-5">
                <div className="text-white font-semibold">Challenges</div>
                <div className="text-sm text-white/55 mt-1">Keep the momentum</div>

                <div className="mt-4 space-y-3">
                  {(dash?.achievements || []).length ? (
                    dash.achievements.slice(0, 3).map((ch, idx) => (
                      <div
                        key={ch.id || ch.challenge_id || idx}
                        className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3"
                      >
                        <div className="text-sm text-white/80 font-medium">
                          {ch.title || ch.name || "Challenge"}
                        </div>
                        <div className="text-xs text-white/55 mt-1">
                          {ch.description || "—"}
                        </div>
                        {typeof ch.progress === "number" && typeof ch.goal === "number" ? (
                          <div className="mt-3">
                            <ProgressBar value={ch.progress} max={ch.goal || 1} />
                            <div className="mt-1 text-[11px] text-white/45 tabular-nums">
                              {ch.progress}/{ch.goal}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-white/55">
                      No active challenges right now.
                    </div>
                  )}

                  <div className="rounded-xl bg-gradient-to-r from-white/[0.08] to-white/[0.04] ring-1 ring-white/10 p-3">
                    <div className="flex items-center gap-2 text-sm text-white/85 font-medium">
                      <TrendingUp className="h-4 w-4" />
                      Next win
                    </div>
                    <div className="text-xs text-white/55 mt-1">
                      Do 3 interviews this week + review the weakest category after each one.
                    </div>
                  </div>
                </div>
              </SoftCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
